import { PROVIDER_LABELS_TH, PROVIDERS, type Provider } from '@natashu/shared';
import { Camera, CircleHelp, FlaskConical, MessageCircle, Music2, Send, Smartphone, type LucideIcon } from 'lucide-react';
import type { CSSProperties } from 'react';
import { cx } from '../../lib/cx.ts';
import './ProviderIcon.css';

/**
 * Channel marks are simple coloured tiles with a generic glyph — official logos are not
 * redrawn. The accessible name is the Thai provider label.
 */
const MARKS: Record<Provider, { icon?: LucideIcon; glyph?: string }> = {
  line: { icon: MessageCircle },
  facebook: { glyph: 'f' },
  instagram: { icon: Camera },
  tiktok: { icon: Music2 },
  telegram: { icon: Send },
  sms: { icon: Smartphone },
  mock: { icon: FlaskConical },
};

function isProvider(value: string): value is Provider {
  return (PROVIDERS as readonly string[]).includes(value);
}

export interface ProviderIconProps {
  provider: Provider | string;
  size?: number;
  showLabel?: boolean;
  /** Visible "DEMO" tag for the mock provider (keep on unless space is tiny, e.g. avatar badges). */
  demoTag?: boolean;
  className?: string;
}

export function ProviderIcon({ provider, size = 20, showLabel = false, demoTag = true, className }: ProviderIconProps) {
  const known = isProvider(provider) ? provider : null;
  const mark = known ? MARKS[known] : { icon: CircleHelp };
  const label = known ? PROVIDER_LABELS_TH[known] : provider;
  const Icon = mark.icon;
  return (
    <span className={cx('provider', className)}>
      <span
        className={cx('provider__mark', `provider__mark--${known ?? 'unknown'}`)}
        role="img"
        aria-label={label}
        title={label}
        style={{ '--provider-size': `${size}px` } as CSSProperties}
      >
        {Icon ? (
          <Icon size={Math.round(size * 0.6)} strokeWidth={2.25} aria-hidden="true" />
        ) : (
          <span className="provider__glyph" aria-hidden="true">
            {mark.glyph}
          </span>
        )}
      </span>
      {showLabel && <span className="provider__label">{label}</span>}
      {known === 'mock' && demoTag && !showLabel && <span className="provider__demo">DEMO</span>}
    </span>
  );
}
